import { createFormField } from '../common';
import { blue, orange, purple } from '@material-ui/core/colors';
import { Grid, TextField } from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
} from '@material-ui/lab';
import companyTypesMaster from 'assets/company-types';
import { flatten } from 'lodash';
import styled from 'styled-components';
import { useState, useMemo, useEffect } from 'react';

interface Option {
  label: string;
  value: number;
}
interface ChildOption extends Option {
  parent: number;
  parentName: string;
}
export interface CategorySet {
  types: Option[];
  subTypes: ChildOption[];
  services: ChildOption[];
}

export const CompanyTypes = ({
  value = null,
  onChange = null,
}: {
  value?: CategorySet;
  onChange?: (set: CategorySet) => any;
}) => {
  const [categories, setCategories] = useState<CategorySet>(() =>
    getValidValue(value, {
      types: [],
      subTypes: [],
      services: [],
    })
  );

  useEffect(() => {
    if (value !== categories) setCategories(getValidValue(value, categories));
  }, [value]);

  const handleChange = useMemo(() => {
    /**
     * Special method to trigger onchange handler.
     */
    const updateCategories = async (updater: (set: CategorySet) => CategorySet) => {
      const nextVal = await new Promise<CategorySet>(r => {
        setCategories(c => {
          const toUpdate = updater(c);
          setImmediate(r, toUpdate);
          return toUpdate;
        });
      });
      onChange && onChange(nextVal);
    };

    const createHandler = (setter: 'subTypes' | 'services') => (
      _event: any,
      value: any,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<{
        label: string;
        value: number;
        parent: number;
        parentName: string;
      }>
    ) => {
      if (reason === 'select-option') {
        updateCategories(c => {
          const option = details.option;
          const inTypes = c.types.find(t => t.value === option.parent);
          let newTypes = c.types;
          if (!inTypes) {
            newTypes = newTypes.concat([
              categoryOptions.types.find(t => t.value === option.parent),
            ]);
          }
          return { ...c, types: newTypes, [setter]: value };
        });
        return;
      }
      updateCategories(c => ({ ...c, [setter]: value }));
    };
    const typesHandler = (
      _event: any,
      value: { label: string; value: number }[],
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<{ label: string; value: number }>
    ) => {
      if (reason === 'remove-option') {
        const removeUnused = s => s.filter(e => e.parent !== details.option.value);
        updateCategories(c => ({
          types: value,
          subTypes: removeUnused(c.subTypes),
          services: removeUnused(c.services),
        }));
        return;
      } else if (reason === 'clear') {
        updateCategories(() => ({ types: value, subTypes: [], services: [] }));
        return;
      }
      updateCategories(c => ({ ...c, types: value }));
    };
    return {
      types: typesHandler,
      services: createHandler('services'),
      subTypes: createHandler('subTypes'),
    };
  }, [setCategories, onChange]);

  return (
    <Grid container spacing={2}>
      <Grid xs={4} item>
        <CompanyTypeStyles>
          <Autocomplete<Option, true, false, false>
            multiple
            value={categories.types}
            onChange={handleChange.types}
            disableCloseOnSelect
            getOptionSelected={getOptionSelected}
            options={categoryOptions.types}
            getOptionLabel={getLabel}
            size="small"
            renderInput={TypesTextField}
          />
        </CompanyTypeStyles>
      </Grid>
      <Grid xs={4} item>
        <CompanySubTypeStyles>
          <Autocomplete
            multiple
            onChange={handleChange.subTypes}
            value={categories.subTypes}
            disableCloseOnSelect
            getOptionSelected={getOptionSelected}
            options={categoryOptions.subTypes}
            getOptionLabel={getLabel}
            groupBy={getChildGroupBy}
            size="small"
            renderInput={SubTypesTextField}
          />
        </CompanySubTypeStyles>
      </Grid>
      <Grid xs={4} item>
        <CompanyServicesStyles>
          <Autocomplete
            multiple
            disableCloseOnSelect
            getOptionSelected={getOptionSelected}
            options={categoryOptions.services}
            value={categories.services}
            onChange={handleChange.services}
            getOptionLabel={getLabel}
            groupBy={getChildGroupBy}
            size="small"
            renderInput={ServicesTextField}
          />
        </CompanyServicesStyles>
      </Grid>
    </Grid>
  );
};

export const CompanyTypesField = createFormField(CompanyTypes);

const getValidValue = (value: any, compare: CategorySet): CategorySet => {
  if (value && typeof value === 'object') {
    const newValue: any = {};
    ['types', 'subTypes', 'services'].forEach(
      t => (newValue[t] = Array.isArray(value[t]) ? value[t] : compare[t])
    );
    return newValue;
  }
  return compare;
};

const getOptionSelected = (value: { value: number }, other: { value: number }) =>
  value.value === other.value;
const getLabel = (opt: { label: string }) => opt.label;
const getChildGroupBy = (option: { parentName: string }) => option.parentName;

const createTextFields = (label: string, helperText: string = null) => props => (
  <TextField {...props} helperText={helperText} label={label} variant="outlined" />
);

const ServicesTextField = createTextFields(
  'Services',
  'The services offered by this company, select from dropdown'
);
const SubTypesTextField = createTextFields(
  'Subtypes',
  'The subtypes of the company, select from dropdown.'
);
const TypesTextField = createTextFields('Types', 'The types of the company, select from dropdown');

const createStyleContainer = (background: string) => styled.div`
  .MuiChip-root.MuiAutocomplete-tag {
    background: ${background};
    color: white;
  }
  .MuiChip-deleteIcon {
    color: #eee;
  }
`;

const CompanyTypeStyles = createStyleContainer(purple[800]);
const CompanySubTypeStyles = createStyleContainer(orange[800]);
const CompanyServicesStyles = createStyleContainer(blue[800]);

const categoryOptions: CategorySet = {
  types: companyTypesMaster.map(({ id, type }) => ({ label: type, value: id })),
  subTypes: flatten(
    companyTypesMaster
      .filter(t => t.child.subtypes.length)
      .map(t =>
        t.child.subtypes.map(s => ({
          label: s.subtype,
          value: s.id,
          parent: t.id,
          parentName: t.type,
        }))
      )
  ),
  services: flatten(
    companyTypesMaster
      .filter(t => t.child.services.length)
      .map(t =>
        t.child.services.map(s => ({
          label: s.service,
          value: s.id,
          parent: t.id,
          parentName: t.type,
        }))
      )
  ),
};
